/**
 * 시딩 상세 — service DTO → presentation `SeedingCampaignDetail` 매핑 공용 헬퍼.
 *
 * service(`@musinsa/services-curator`)는 raw 에 가까운 lean DTO 만 응답한다.
 * 화면이 소비하는 `SeedingCampaignDetail` 은 가공된 표시 텍스트(라벨/기한/카운트다운/CTA)를
 * 다수 포함하므로, 그 파생 로직을 본 파일에 모은다.
 *
 * curator-feature skill 규칙 #7 은 "derived 텍스트는 Service DTO 에서"를 권장하지만,
 * 이번 작업은 검증된 service DTO 계약을 건드리지 않기 위해 features adapter 에서 파생한다
 * (사용자 결정 2026-06-01).
 *
 * ⚠ BE 응답에 아예 없는 필드는 안전 fallback + `// NOTE: BE DTO 미제공` 으로 표기한다.
 * 미제공 목록: brand 이름/로고, 상품 선택 옵션 텍스트, 택배사/송장번호,
 * 배송지 zipCode(recruit 상세), 큐레이터 선택 SNS 채널, 일부 상태별 안내 copy.
 */

import type {
  GetSeedingProposalDetailServiceResponse,
  GetSeedingRecruitmentDetailServiceResponse,
} from '@musinsa/services-curator';
import type {
  SeedingBrand,
  SeedingContentGuide,
  SeedingContentSubmission,
  SeedingContentType,
  SeedingContentTypeData,
  SeedingContentTypeOption,
  SeedingPenalty,
  SeedingPrimaryCta,
  SeedingProduct,
  SeedingProductSlotMember,
  SeedingShippingAddress,
  SeedingShippingDetailRow,
  SeedingShippingTracking,
  SeedingSnsChannel,
} from '../types';

type ServiceCampaign =
  | GetSeedingRecruitmentDetailServiceResponse['campaign']
  | GetSeedingProposalDetailServiceResponse['campaign'];
type ServiceGoods =
  | GetSeedingRecruitmentDetailServiceResponse['goods']
  | GetSeedingProposalDetailServiceResponse['goods'];
type ServiceContentType = ServiceCampaign['contentTypes'][number]['type'];
type ServiceDeliveryAddress = NonNullable<
  GetSeedingRecruitmentDetailServiceResponse['deliveryAddress']
>;
type ServiceFulfillments =
  | GetSeedingRecruitmentDetailServiceResponse['fulfillments']
  | GetSeedingProposalDetailServiceResponse['fulfillments'];
type ServiceContentSubmissions =
  | GetSeedingRecruitmentDetailServiceResponse['contentSubmissions']
  | GetSeedingProposalDetailServiceResponse['contentSubmissions'];

/** content guide 영역 고정 카피 (전 상태 동일 — mock 검증). */
export const HASHTAG_DESCRIPTION = '필수 해시태그';
export const HASHTAG_NOTICE = '#광고 해시태그는 게시물 본문 최상단에 첫 번째로 작성해 주세요.';

/** 콘텐츠 유형 enum → 표시 라벨. */
const CONTENT_TYPE_LABEL: Record<ServiceContentType, string> = {
  IMAGE: '이미지',
  SHORT_FORM: '숏폼 영상',
  LONG_FORM: '롱폼 영상',
  TEXT: '텍스트',
  // NOTE: presentation `SeedingContentType` 에는 BLOG_POST 가 없어 라벨만 보유하고 TEXT 로 narrow.
  BLOG_POST: '블로그',
};

/** SNS 채널 enum → 표시 라벨. */
export const SNS_CHANNEL_LABEL: Record<SeedingSnsChannel, string> = {
  INSTAGRAM: '인스타그램',
  YOUTUBE: '유튜브',
  TWITTER: 'X(트위터)',
  TIKTOK: '틱톡',
  NAVER_BLOG: '네이버 블로그',
  THREADS: '스레드',
  SNAP: '스냅',
  MUSINSA_SNAP: '무신사 스냅',
};

export const contentTypeLabel = (type: ServiceContentType): string => CONTENT_TYPE_LABEL[type];

/**
 * 콘텐츠 유형 표시·기본값 순서 (SID-4393). BE 응답 순서와 무관하게 항상 이 순서로 정렬하고,
 * 정렬된 "선택 가능 목록"의 첫 번째를 기본 선택값으로 쓴다. (숏폼 → 롱폼 → 이미지 → 텍스트)
 * presentation 타입 기준 — BLOG_POST 는 toPresentationContentType 로 TEXT 에 합류한다.
 */
const CONTENT_TYPE_ORDER: Record<SeedingContentType, number> = {
  SHORT_FORM: 0,
  LONG_FORM: 1,
  IMAGE: 2,
  TEXT: 3,
};

export const snsChannelLabel = (channel: SeedingSnsChannel | string): string =>
  SNS_CHANNEL_LABEL[channel as SeedingSnsChannel] ?? channel;

/**
 * TC-093 — SNS 채널별 제작 가능 콘텐츠 유형(presentation 4종 기준). 스펙 채널 4종만 매핑하고,
 * 나머지(NAVER_BLOG / THREADS / SNAP / MUSINSA_SNAP)는 매핑이 없어 교집합에서 자연히 제외된다.
 */
const CHANNEL_CONTENT_TYPES: Partial<Record<SeedingSnsChannel, SeedingContentType[]>> = {
  INSTAGRAM: ['IMAGE', 'SHORT_FORM'],
  YOUTUBE: ['SHORT_FORM', 'LONG_FORM'],
  TWITTER: ['IMAGE', 'TEXT'],
  TIKTOK: ['SHORT_FORM', 'TEXT'],
};

/**
 * frozen 콘텐츠 카드의 SNS 채널 표시 — 큐레이터는 채널을 직접 고르지 않으므로, "선택한 콘텐츠
 * 유형을 제작 가능한 채널" ∩ "캠페인 허용 채널(snsChannels)" 을 노출한다. (TC-093)
 * - 교집합 1개: "틱톡" (그 유형은 해당 채널로만 올릴 수 있어 사실상 확정)
 * - 교집합 ≥2개: "틱톡, X(트위터) 중 택 1"
 * - 교집합 0개(데이터 불일치): undefined → 카드에서 '-'
 */
export const resolveFrozenSnsChannelLabel = (
  snsChannels: readonly SeedingSnsChannel[],
  selectedType: SeedingContentType,
): string | undefined => {
  const matched = snsChannels.filter((channel) =>
    CHANNEL_CONTENT_TYPES[channel]?.includes(selectedType),
  );
  if (matched.length === 0) return undefined;
  const labels = matched.map(snsChannelLabel);
  return labels.length > 1 ? `${labels.join(', ')} 중 택 1` : labels[0];
};

/**
 * service contentType(BLOG_POST 포함) → presentation `SeedingContentType`(4종) 로 narrow.
 * BLOG_POST 는 화면 타입에 없어 TEXT 로 대체한다.
 */
export const toPresentationContentType = (type: ServiceContentType): SeedingContentType => {
  switch (type) {
    case 'IMAGE':
    case 'SHORT_FORM':
    case 'LONG_FORM':
    case 'TEXT':
      return type;
    default:
      return 'TEXT';
  }
};

/** "배송 완료일로부터 N일 이내 업로드". (마감 확정 전 정책 안내) */
export const uploadDeadlineText = (days: number): string =>
  `배송 완료일로부터 ${days}일 이내 업로드`;

/**
 * "YYYY-MM-DD까지 업로드" — 실제 콘텐츠 제출 마감일(확정) 표기. (SID-4319, Figma 216-52510)
 * deadlineAt 은 BE KST 문자열("YYYY-MM-DD HH:mm:ss") → 날짜부만 사용.
 */
export const uploadDeadlineDateText = (deadlineAt: string): string =>
  `${deadlineAt.slice(0, 10)}까지 업로드`;

/**
 * BE 날짜 포맷("YYYY-MM-DD HH:mm:ss", KST, offset 표기 없음) → ms epoch.
 * services `parseKstDateTime` 와 동일 로직 (features 로는 export 되지 않아 재정의).
 */
export const parseKstDateTime = (value: string): number =>
  new Date(`${value.replace(' ', 'T')}+09:00`).getTime();

/** "2026-05-20 00:00:00" → "26.05.20" (2자리 연도). (SID-4327) */
export const formatDateDot = (value: string): string => value.slice(2, 10).replace(/-/g, '.');

/** KST 기준 요일 한 글자 ("월"~"일"). */
const weekdayKo = (value: string): string =>
  new Date(parseKstDateTime(value)).toLocaleDateString('ko-KR', {
    weekday: 'short',
    timeZone: 'Asia/Seoul',
  });

/** 발송 예정일 날짜 연도 표기 모드. */
type ShippingDateYear = 'full' | 'short' | 'none';

/**
 * 발송 예정일 표기용 날짜+요일 포맷 (항상 "… (요일)" 공백 포함). 요일 KST 기준.
 * - 'full'  (제안 수락전/완료): "YYYY.MM.DD (수)"
 * - 'short' (제안 배송중):       "YY.MM.DD (수)"
 * - 'none'  (모집):              "MM.DD (수)"
 */
export const formatShippingDate = (value: string, year: ShippingDateYear): string => {
  const [y, m, d] = value.slice(0, 10).split('-');
  const datePart =
    year === 'full' ? `${y}.${m}.${d}` : year === 'short' ? `${y.slice(2)}.${m}.${d}` : `${m}.${d}`;
  return `${datePart} (${weekdayKo(value)})`;
};

/**
 * 모집 기간 range 표기 — "YYYY.MM.DD (요일) - YYYY.MM.DD (요일)". 시작·종료 모두 연도(YYYY) 표기.
 * (종료일 연도 생략 → 노출로 정정. Figma 정합. CMQA-7158)
 * API `campaign.recruitmentStartAt` / `recruitmentEndAt` 기반.
 */
export const formatRecruitmentPeriod = (start: string, end: string): string =>
  `${formatShippingDate(start, 'full')} - ${formatShippingDate(end, 'full')}`;

/**
 * 모집형 발송 예정일 표시값 — "MM.DD (요일) / 선정일로부터 영업일 N일 이내". (상품준비중/배송중 전용)
 * N(`guideDays`)은 BE 정책 상수(`shippingDeadlineDays`) — FE 하드코딩 금지.
 * 날짜 없으면(추정 실패) 안내 문구만, 안내도 없으면 빈 문자열.
 */
export const formatRecruitShippingPlanned = (
  date: string | null | undefined,
  guideDays: number | null | undefined,
): string => {
  const guide = guideDays != null ? `선정일로부터 영업일 ${guideDays}일 이내` : '';
  if (date) {
    const datePart = formatShippingDate(date, 'none');
    return guide ? `${datePart} / ${guide}` : datePart;
  }
  return guide;
};

/**
 * 제안형 발송 예정일 표시값 — "‹날짜› ‹suffix›". 날짜 없으면(추정 실패) '-'.
 * - 수락전/수락완료: year='full', suffix='이내'  → "YYYY.MM.DD (수) 이내"
 * - 배송중:          year='short', suffix='예정' → "YY.MM.DD (수) 예정"
 */
export const formatProposeShippingPlanned = (
  date: string | null | undefined,
  year: ShippingDateYear,
  suffix: string,
): string => (date ? `${formatShippingDate(date, year)} ${suffix}` : '-');

/**
 * brand 정보 — BE 는 `campaign.brandId` 만 응답한다.
 * NOTE: BE DTO 미제공 — 이름/로고. brandId 를 이름 fallback, 로고는 빈 문자열.
 */
export const buildBrand = (campaign: ServiceCampaign): SeedingBrand => ({
  // 표시용 브랜드명(예: '032씨') 우선, 미제공 시 brandId 코드('032c') 폴백. (appbar 타이틀 "{브랜드명} 캠페인")
  name: campaign.brandName || campaign.brandId,
  logoUrl: '',
  // 상품 카드 브랜드명 클릭 → 브랜드 스토어 홈 진입용. (QAT-39448)
  brandId: campaign.brandId,
});

/**
 * service penalty → presentation `SeedingPenalty`.
 *
 * NOTE(SID-4178): BE 가 패널티 미보유 큐레이터에도 `penalty: { reason: '' }`(빈 사유)를 내려
 * 모든 상세 상단에 배너가 오노출됐다. 객체 존재만으로 활성 판단하지 않고, reason 이
 * 실제 텍스트일 때만 활성 패널티로 간주한다 (null / 공백 사유 → 미노출).
 */
/** BE penalty.reason 은 식별 코드(UPPER_SNAKE)를 내려준다 (QAT-39734). 화면 노출 전 친화 문구로 매핑. */
const PENALTY_REASON_LABELS: Record<string, string> = {
  // 런칭 시 패널티 사유 1종 (PRD §3-1 콘텐츠 제출 기한 초과).
  CONTENT_SUBMISSION_DEADLINE_EXCEEDED: '콘텐츠 제출 기한 초과',
};
/** 미매핑 코드 → raw 코드 노출 금지, 일반 안내문으로 대체. */
const PENALTY_REASON_FALLBACK = '신규 신청·수락이 제한된 상태입니다.';
const PENALTY_CODE_PATTERN = /^[A-Z0-9_]+$/;

/** penalty.reason 을 다이얼로그 노출용 문구로 변환. 코드는 매핑/폴백, 이미 친화 문자열이면 그대로. */
export const toPenaltyReasonLabel = (raw: string): string => {
  const reason = raw.trim();
  if (PENALTY_REASON_LABELS[reason]) return PENALTY_REASON_LABELS[reason];
  if (PENALTY_CODE_PATTERN.test(reason)) return PENALTY_REASON_FALLBACK;
  return reason;
};

export const buildPenalty = (
  penalty: { reason: string } | null | undefined,
): SeedingPenalty | null => {
  const reason = penalty?.reason?.trim();
  // 빈 사유 → null(SID-4178). 사유 코드는 친화 문구로 매핑(QAT-39734).
  return reason ? { reason: toPenaltyReasonLabel(reason) } : null;
};

/**
 * 신청/수락 CTA 는 패널티 활성 여부와 무관하게 항상 enabled — 패널티는 클릭 시점
 * 유효성 검사(evaluateRecruitApplyValidation → PENALTY 다이얼로그)로 안내한다. (SID-4163)
 *
 * 시그니처는 호출부 호환을 위해 유지하되, 항상 원본 CTA 를 그대로 반환한다.
 * (이전엔 패널티 시 disabled:true 로 강제했음 — PRD 5-4)
 */
export const applyPenaltyToCta = (
  cta: SeedingPrimaryCta | null,
  _penaltyActive: boolean,
): SeedingPrimaryCta | null => cta;

/** 브랜드 요청사항 미입력 시 노출하는 기본 가이드 문구 (PRD §4-1-5 / TC-076). */
export const DEFAULT_BRAND_REQUEST = '협찬 상품이 잘 보이는 스타일 콘텐츠를 제작해 주세요.';

/** content guide — hashtagText 는 service 가 조립, brandRequest 는 requestNote(미입력 시 기본 가이드). */
export const buildContentGuide = (campaign: ServiceCampaign): SeedingContentGuide => ({
  hashtagDescription: HASHTAG_DESCRIPTION,
  hashtagText: campaign.hashtagText,
  hashtagNotice: HASHTAG_NOTICE,
  brandRequest: campaign.requestNote?.trim() ? campaign.requestNote : DEFAULT_BRAND_REQUEST,
});

/**
 * goods[] → presentation products[]. **slotNo 단위로 그룹핑** 한다 (PRD 5-2):
 * 같은 `slotNo` = 아더컬러 그룹 = 1 슬롯 카드. 슬롯에 묶인 goods 가
 * - 1개 → 1depth ("옵션 {verb}")
 * - 2개+ → 아더컬러 2depth ("상품/옵션 {verb}": 상품(컬러) 선택 → 옵션 선택)
 *
 * NOTE: `selectedOption` — 신청/수락 시 선택한 옵션 표시 문자열. 아더컬러 그룹(slotNo 동일)에서는
 *   실제 선택된 색상 goods(`selected === true`)에만 옵션이 실려 오므로 그 goods 에서 읽는다. (QAT-39515)
 * @param showCategory 카테고리 라벨 노출 상태(신청 단계)에서만 category 채움
 * @param actionVerb   CTA 동사("선택"=옵션 미선택 / "변경"=선택 완료) — null 이면 read-only(버튼 미노출)
 */
export const mapGoodsToProducts = (
  goods: ServiceGoods,
  { showCategory, actionVerb }: { showCategory: boolean; actionVerb: '선택' | '변경' | null },
): SeedingProduct[] => {
  // slotNo 동일 = 아더컬러 그룹 → 입력 순서를 보존하며 슬롯별로 goods 를 묶는다.
  const slots = new Map<number, ServiceGoods[number][]>();
  for (const item of goods) {
    const grouped = slots.get(item.slotNo);
    if (grouped) grouped.push(item);
    else slots.set(item.slotNo, [item]);
  }

  const products = [...slots.values()].map((groupedGoods) => {
    const representative = groupedGoods[0];
    if (!representative) {
      // slots 값은 생성 시 항상 [item] 이라 도달 불가 — 타입 narrowing 용 가드.
      throw new Error('시딩 상품 슬롯은 최소 1개 goods 를 가진다.');
    }
    const isMultiGoods = groupedGoods.length >= 2;
    // 슬롯 전체 품절(아더컬러 전 색상 품절)일 때만 슬롯을 품절 처리 → 버튼 미노출.
    const soldOut = groupedGoods.every((item) => item.soldOut);
    // 선택된 goods(`selected`)의 옵션 텍스트. 단품(옵션 없음)은 selected=true 라도 null 이라 미노출.
    const selectedOption = groupedGoods.find((item) => item.selected)?.selectedOption ?? null;
    const members: SeedingProductSlotMember[] = groupedGoods.map((item) => ({
      goodsNo: item.goodsNo,
      brand: item.brandName,
      name: item.goodsName,
      imageUrl: item.imageUrl,
      webUrl: item.webUrl,
      price: item.price,
      soldOut: item.soldOut,
    }));
    const actionLabel = actionVerb
      ? isMultiGoods
        ? `상품/옵션 ${actionVerb}`
        : `옵션 ${actionVerb}`
      : null;

    return {
      productId: representative.goodsNo,
      slotNo: representative.slotNo,
      members,
      isMultiGoods,
      brand: representative.brandName,
      name: representative.goodsName,
      category: showCategory ? representative.category : '',
      imageUrl: representative.imageUrl,
      webUrl: representative.webUrl,
      price: representative.price,
      selectedOption,
      soldOut,
      actionLabel: soldOut ? null : actionLabel,
    };
  });

  // groupby — 같은 카테고리가 연속되는 상품은 첫 상품에만 카테고리(헤더)를 남긴다.
  // 시딩 상세 상품 리스트 공통(모집형·제안형 PageView 가 동일하게 product.category 를 헤더로 렌더).
  let groupCategory = '';
  for (const product of products) {
    if (product.category === groupCategory) product.category = '';
    else groupCategory = product.category;
  }

  return products;
};

/**
 * 배송지 — DTO 는 name / phone / address1(기본) / address2(상세) / addressName(별칭) +
 * 0610 부터 deliveryAddressId / zipcode 보유. addressName(별칭)은 화면 타입에 슬롯이 없어 미사용.
 */
export const buildShippingAddress = (
  deliveryAddress: ServiceDeliveryAddress | null,
  editable: boolean,
): SeedingShippingAddress => ({
  deliveryAddressId: deliveryAddress?.deliveryAddressId ?? null,
  recipient: deliveryAddress?.name ?? '',
  phone: deliveryAddress?.phone ?? '',
  zipCode: deliveryAddress?.zipcode ?? '',
  address: deliveryAddress?.address1 ?? '',
  addressDetail: deliveryAddress?.address2 ?? '',
  addressName: deliveryAddress?.addressName ?? null,
  memo: deliveryAddress?.memo ?? null,
  editable,
  // deliveryAddress == null = 기본배송지 미설정 → "미설정" 안내 노출
  registered: deliveryAddress !== null,
});

/** editable content type 카드 — 신청 전(옵션 선택 가능) 상태. */
export const buildEditableContentType = (
  campaign: ServiceCampaign,
  selectedType: SeedingContentType | null,
  message: string,
): Extract<SeedingContentTypeData, { mode: 'editable' }> => {
  const options: SeedingContentTypeOption[] = campaign.contentTypes
    .map((option) => ({
      value: toPresentationContentType(option.type),
      label: contentTypeLabel(option.type),
      deadline: uploadDeadlineText(option.uploadDeadlineDays),
      uploadDeadlineDays: option.uploadDeadlineDays,
    }))
    // BE 순서와 무관하게 고정 순서로 정렬(숏폼→롱폼→이미지→텍스트). (SID-4393)
    .sort((a, b) => CONTENT_TYPE_ORDER[a.value] - CONTENT_TYPE_ORDER[b.value]);

  return {
    mode: 'editable',
    options,
    // 기본 선택값 = 명시값 우선, 없으면 정렬된 선택 가능 유형의 첫 번째. (SID-4393)
    selectedType: selectedType ?? options[0]?.value ?? null,
    message,
    messagePlaceholder:
      '나만의 스타일링 포인트나 콘텐츠 강점 등 브랜드에 전달하고 싶은 메시지를 자유롭게 적어주세요.',
    messageMaxLength: 200,
  };
};

/** frozen content type 카드 — 확정(read-only) 상태. */
export const buildFrozenContentType = (
  campaign: ServiceCampaign,
  selectedType: ServiceContentType,
  message: string,
  // 콘텐츠 제출 마감일(확정) — 있으면 "YYYY-MM-DD까지 업로드", 없으면 정책 문구. (SID-4319)
  contentSubmissionDeadlineAt: string | null = null,
): Extract<SeedingContentTypeData, { mode: 'frozen' }> => {
  const matched = campaign.contentTypes.find((option) => option.type === selectedType);

  return {
    mode: 'frozen',
    selectedType: toPresentationContentType(selectedType),
    selectedTypeLabel: contentTypeLabel(selectedType),
    message,
    // 큐레이터는 채널을 직접 고르지 않는다 → 선택한 콘텐츠 유형을 제작 가능한 허용 채널만 노출.
    // 교집합 1개면 단일("틱톡"), ≥2개면 "틱톡, X(트위터) 중 택 1". (TC-093 / selectedType ∩ snsChannels)
    selectedSnsChannelLabel: resolveFrozenSnsChannelLabel(
      campaign.snsChannels,
      toPresentationContentType(selectedType),
    ),
    // 마감 확정 시 실제 날짜, 그 전엔 정책 문구(배송 완료일로부터 N일). (SID-4319)
    uploadDeadlineNotice: contentSubmissionDeadlineAt
      ? uploadDeadlineDateText(contentSubmissionDeadlineAt)
      : matched
        ? uploadDeadlineText(matched.uploadDeadlineDays)
        : undefined,
  };
};

/** fulfillments[] 의 "가장 낮은(덜 진행된) 단계" 를 화면 배송 단계로 집계. */
const lowestFulfillmentStage = (
  fulfillments: ServiceFulfillments,
): 'SHIPPING_READY' | 'SHIPPING' | 'DELIVERED' | null => {
  if (fulfillments.length === 0) return null;
  if (fulfillments.some((f) => f.status === 'SHIPPING_READY')) return 'SHIPPING_READY';
  if (fulfillments.some((f) => f.status === 'SHIPPING')) return 'SHIPPING';
  if (fulfillments.every((f) => f.status === 'DELIVERED')) return 'DELIVERED';
  return 'SHIPPING';
};

/**
 * 배송 상태 행 구성 — "배송 상태" + (BE 제공 시) "택배사" + "송장 번호"(복사 가능).
 * 택배사/송장번호는 fulfillment.courierName/invoiceNo (API 0610, 미등록 시 null)라 있을 때만 행을 추가한다.
 * (Figma 1-22924 — 배송중 케이스: 배송 상태 / 택배사 / 송장 번호)
 */
const buildShippingDetailRows = (
  statusValue: string,
  fulfillments: ServiceFulfillments,
): SeedingShippingDetailRow[] => {
  // 송장정보가 있는 출고건(다건이면 첫 출고건) 기준으로 택배사/송장번호 노출.
  const shipped = fulfillments.find((f) => f.invoiceNo) ?? fulfillments[0];
  const rows: SeedingShippingDetailRow[] = [
    { label: '배송 상태', value: statusValue, copyable: false },
  ];
  if (shipped?.courierName) {
    rows.push({ label: '택배사', value: shipped.courierName, copyable: false });
  }
  if (shipped?.invoiceNo) {
    rows.push({ label: '송장 번호', value: shipped.invoiceNo, copyable: true });
  }
  return rows;
};

/**
 * 배송 추적 카드 — SHIPPING / DELIVERED 단계에서만 노출(준비중·미출고는 null).
 *
 * 타이틀 옆 상태 배지는 노출하지 않고(배송중/배송완료 badge 제거 — figma 1-22924),
 * 배송 상태는 details 의 "배송 상태" 행으로 표기한다. 택배사/송장번호는 BE 제공 시 함께 노출.
 */
export const buildShippingTracking = (
  fulfillments: ServiceFulfillments,
  contentSubmissions: ServiceContentSubmissions,
): SeedingShippingTracking | null => {
  const stage = lowestFulfillmentStage(fulfillments);
  if (stage === null || stage === 'SHIPPING_READY') return null;

  if (stage === 'SHIPPING') {
    return {
      status: 'SHIPPING',
      statusLabel: null,
      labelVariant: null,
      banners: [],
      details: buildShippingDetailRows('배송중', fulfillments),
    };
  }

  return {
    status: 'DELIVERED',
    statusLabel: null,
    labelVariant: null,
    banners: [],
    details: buildShippingDetailRows('배송완료', fulfillments),
  };
};

/**
 * 콘텐츠 링크 제출 영역 — 두 경로로 노출.
 * - 제출 완료(완료 캠페인): contentSubmissions 가 있으면 배송 단계와 무관하게 read-only 로 노출.
 * - 미제출: 배송 단계(SHIPPING/DELIVERED) 진입 + 콘텐츠 유형 확정 시 "제출하기" 로 노출.
 *
 * NOTE(SID-4179): 완료(CONTENT_SUBMITTED) 캠페인은 BE 가 fulfillments 를 비워 응답하는 경우가 있어
 * lowestFulfillmentStage 게이트에 걸려 제출 완료 구좌가 통째로 사라졌다. 제출 이력이 있으면
 * 제출 단계를 이미 지난 것이므로 fulfillment 단계와 무관하게 read-only 로 노출한다.
 * NOTE: BE DTO 미제공 — 미제출 항목의 대상 SNS 채널. snsChannels[0] 로 fallback.
 */
export const buildContentSubmission = (
  campaign: ServiceCampaign,
  fulfillments: ServiceFulfillments,
  contentSubmissions: ServiceContentSubmissions,
  selectedContentType: ServiceContentType | null,
  isCompleted: boolean,
): SeedingContentSubmission | null => {
  // 제출 이력이 있으면 완료 캠페인 — 배송 단계 게이트 우회하고 read-only 로 노출. (SID-4179)
  if (contentSubmissions.length > 0) {
    return {
      isComplete: isCompleted,
      items: contentSubmissions.map((submission) => ({
        channel: submission.channel,
        contentType: toPresentationContentType(submission.contentType),
        submittedUrl: submission.contentUrl,
        label: '콘텐츠 링크 제출',
        actionLabel: '확인하기',
      })),
    };
  }

  // 완료(COMPLETED) 캠페인은 콘텐츠 미제출이어도 제출 구좌(제출하기)를 노출해야 한다.
  // fulfillments 가 비정상(빈 배열 등)이면 stage 게이트에서 걸려 구좌가 사라지던 문제.
  // (SID-4179 / SID-4178 — 완료일 있으나 업로드 콘텐츠 없음 케이스)
  const stage = lowestFulfillmentStage(fulfillments);
  if (stage !== 'SHIPPING' && stage !== 'DELIVERED' && !isCompleted) return null;
  if (selectedContentType === null) return null;

  const fallbackChannel = campaign.snsChannels[0] ?? 'INSTAGRAM';
  return {
    isComplete: false,
    items: [
      {
        channel: fallbackChannel,
        contentType: toPresentationContentType(selectedContentType),
        submittedUrl: null,
        label: '콘텐츠 링크 제출',
        actionLabel: '제출하기',
      },
    ],
  };
};
